import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FileCheck,
    Download,
    Loader2,
    Copy,
    Check,
    Clock,
    Banknote,
    Zap,
    UserMinus,
    FileText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import { Modal } from './common';
import {
    EstadoBadge,
    RechazoModal,
    EmptyState,
    TabButton,
    formatFecha,
    FiltrosGH,
    filtrarGH,
    descargarBlob,
} from './gh';

/*
 * Constancias de trabajo.
 * - Sin sueldo: sale al instante, firmada por el coordinador de GH de la sede.
 *   Si todavía no hay firma cargada en /firmas, queda pendiente y la prepara GH.
 * - Con sueldo: la prepara GH (escribe el monto, que sale en número y en letras).
 * - Constancia de egreso: la emite GH para quien ya se fue.
 * Cada una lleva un código que se verifica sin sesión en /verificar/<código>.
 */

const ESTADOS_CONSTANCIA = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' },
];

// Solo GH administra este módulo; admin, TI y captación lo ven como cualquier trabajador
const ROLES_GH = ['gh'];

// ------------------------------------------------------------
// Montos: formato venezolano y en letras (igual que el PDF)
// ------------------------------------------------------------

const formatMonto = (monto) => Number(monto).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// '1.250.750,50' o '1250750.50' -> 1250750.5 (null si no es un número)
const parseMonto = (texto) => {
    let t = String(texto || '').replace(/\s/g, '').replace(/^Bs\.?/i, '');
    if (!t) return null;
    if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
    else if ((t.match(/\./g) || []).length > 1) t = t.replace(/\./g, '');
    const n = Number(t);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const UNIDADES = ['cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
    'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho',
    'diecinueve', 'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco',
    'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos',
    'setecientos', 'ochocientos', 'novecientos'];

const menorMil = (n) => {
    if (n === 100) return 'cien';
    const partes = [];
    if (n >= 100) { partes.push(CENTENAS[Math.floor(n / 100)]); n %= 100; }
    if (n >= 30) {
        const u = n % 10;
        partes.push(DECENAS[Math.floor(n / 10)] + (u ? ` y ${UNIDADES[u]}` : ''));
    } else if (n > 0 || partes.length === 0) {
        partes.push(UNIDADES[n]);
    }
    return partes.join(' ');
};

const apocope = (t) => (t.endsWith('veintiuno') ? `${t.slice(0, -9)}veintiún` : t.endsWith('uno') ? `${t.slice(0, -3)}un` : t);

const enLetras = (n) => {
    if (n < 1000) return menorMil(n);
    if (n < 1000000) {
        const miles = Math.floor(n / 1000);
        const resto = n % 1000;
        const cabeza = miles === 1 ? 'mil' : `${apocope(menorMil(miles))} mil`;
        return cabeza + (resto ? ` ${menorMil(resto)}` : '');
    }
    const millones = Math.floor(n / 1000000);
    const resto = n % 1000000;
    const cabeza = millones === 1 ? 'un millón' : `${apocope(enLetras(millones))} millones`;
    return cabeza + (resto ? ` ${enLetras(resto)}` : '');
};

const montoEnLetras = (monto) => {
    const centavos = Math.round(monto * 100);
    const entero = Math.floor(centavos / 100);
    const cts = centavos % 100;
    let texto = apocope(enLetras(entero));
    if (entero >= 1000000 && entero % 1000000 === 0) texto += ' de';
    return `${texto} bolívares con ${String(cts).padStart(2, '0')}/100 cts.`;
};

const codigoConGuion = (c) => (c ? `${c.slice(0, 5)}-${c.slice(5)}` : '');

const nombreTipo = (c) => {
    if (c.tipo === 'egreso') return 'Constancia de egreso';
    return c.incluir_salario ? 'Constancia con sueldo' : 'Constancia sin sueldo';
};

const descargarConstancia = (c) => descargarBlob(
    `/api/constancias-trabajo/${c.id}/pdf`,
    `constancia_${(c.empleado_nombre || 'trabajo').replace(/\s+/g, '_')}_${c.id}.pdf`,
);

// ------------------------------------------------------------
// Piezas
// ------------------------------------------------------------

const CodigoVerificacion = ({ codigo }) => {
    const [copiado, setCopiado] = useState(false);
    if (!codigo) return null;
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(codigoConGuion(codigo));
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1600);
        } catch {
            toast.error('No se pudo copiar el código');
        }
    };
    return (
        <button
            type="button"
            onClick={copiar}
            title="Copiar código de verificación"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sura-blue transition-colors"
        >
            <span>Código</span>
            <span className="font-semibold text-gray-900 tracking-wider tabular-nums">{codigoConGuion(codigo)}</span>
            {copiado ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} className="text-gray-400" />}
        </button>
    );
};

const ConstanciaFila = ({ c, showEmpleado = false, onPreparar }) => {
    const [bajando, setBajando] = useState(false);
    const bajar = async () => {
        setBajando(true);
        try {
            await descargarConstancia(c);
        } catch {
            toast.error('No se pudo descargar la constancia');
        } finally {
            setBajando(false);
        }
    };
    return (
        <div className="bg-white rounded-2xl border border-gray-200 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">
                        {showEmpleado ? c.empleado_nombre : nombreTipo(c)}
                    </span>
                    <EstadoBadge estado={c.estado} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                    {showEmpleado && <span>{nombreTipo(c)}</span>}
                    <span>Pedida el {formatFecha(String(c.fecha_solicitud || '').slice(0, 10))}</span>
                    {c.dirigida_a && <span>Para {c.dirigida_a}</span>}
                    {c.estado === 'aprobada' && c.emitida_al_instante && <span>Salió al instante</span>}
                    {c.estado === 'aprobada' && !c.emitida_al_instante && c.aprobado_por_nombre && (
                        <span>Preparada por {c.aprobado_por_nombre}</span>
                    )}
                    {c.estado === 'pendiente' && !showEmpleado && <span>Gestión Humana la está preparando</span>}
                </div>
                {c.motivo_rechazo && (
                    <p className="mt-1.5 text-sm text-red-700">Rechazada: {c.motivo_rechazo}</p>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {c.estado === 'aprobada' && <CodigoVerificacion codigo={c.codigo_verificacion} />}
                {c.estado === 'aprobada' && c.pdf_path && (
                    <button
                        type="button"
                        onClick={bajar}
                        disabled={bajando}
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 text-sm font-medium text-sura-blue hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sura-blue disabled:opacity-50 transition-colors"
                    >
                        {bajando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        Descargar
                    </button>
                )}
                {c.estado === 'pendiente' && onPreparar && (
                    <button
                        type="button"
                        onClick={() => onPreparar(c)}
                        className="px-4 py-2 rounded-xl bg-sura-blue text-white text-sm font-semibold hover:bg-sura-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue transition-colors"
                    >
                        Preparar
                    </button>
                )}
            </div>
        </div>
    );
};

const Opcion = ({ activa, onClick, icon: Icon, titulo, texto }) => (
    <button
        type="button"
        role="radio"
        aria-checked={activa}
        onClick={onClick}
        className={`text-left rounded-2xl border-2 p-4 sm:p-5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue ${
            activa ? 'border-sura-blue bg-sky-50/60' : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
    >
        <div className="flex items-center gap-2.5">
            <Icon size={18} className={activa ? 'text-sura-blue' : 'text-gray-400'} />
            <span className="font-semibold text-gray-900">{titulo}</span>
        </div>
        <p className="mt-1.5 text-sm text-gray-600 leading-snug">{texto}</p>
    </button>
);

const CampoMonto = ({ id, valor, onChange, etiqueta }) => {
    const monto = parseMonto(valor);
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">{etiqueta}</label>
            <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">Bs.</span>
                <input
                    id={id}
                    inputMode="decimal"
                    value={valor}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="300.000,00"
                    className="w-full pl-11 pr-3 py-2.5 rounded-xl border border-gray-300 tabular-nums focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none"
                />
            </div>
            {valor && (
                <p className={`mt-1.5 text-sm ${monto ? 'text-gray-600' : 'text-red-700'}`}>
                    {monto
                        ? <>En la constancia: <span className="font-semibold text-gray-900">Bs. {formatMonto(monto)}</span> ({montoEnLetras(monto)})</>
                        : 'Escribe solo el monto, por ejemplo 300.000,00'}
                </p>
            )}
        </div>
    );
};

const CheckFirma = ({ valor, onChange }) => (
    <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
        <input
            type="checkbox"
            checked={valor}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sura-blue focus:ring-sura-blue"
        />
        <span>Con la firma del coordinador de Gestión Humana de su sede</span>
    </label>
);

// ------------------------------------------------------------
// Pedir (trabajador)
// ------------------------------------------------------------

const PedirConstancia = ({ onPedida }) => {
    const [conSueldo, setConSueldo] = useState(false);
    const [dirigidaA, setDirigidaA] = useState('');
    const [enviando, setEnviando] = useState(false);

    const pedir = async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
            const res = await axiosInstance.post('/api/constancias-trabajo', {
                incluir_salario: conSueldo,
                dirigida_a: dirigidaA.trim() || null,
            });
            const c = res.data;
            if (c.estado === 'aprobada') {
                await descargarConstancia(c);
                toast.success('Tu constancia está lista y se descargó');
            } else {
                toast.success('Pedida. Gestión Humana la prepara y te avisa cuando esté lista');
            }
            setDirigidaA('');
            setConSueldo(false);
            onPedida();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo pedir la constancia');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <form onSubmit={pedir} className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900">Pedir una constancia</h2>
            <div role="radiogroup" aria-label="Tipo de constancia" className="mt-4 grid sm:grid-cols-2 gap-3">
                <Opcion
                    activa={!conSueldo}
                    onClick={() => setConSueldo(false)}
                    icon={Zap}
                    titulo="Sin sueldo"
                    texto="Sale al instante, firmada y lista para descargar."
                />
                <Opcion
                    activa={conSueldo}
                    onClick={() => setConSueldo(true)}
                    icon={Banknote}
                    titulo="Con sueldo"
                    texto="Gestión Humana la prepara y te avisa cuando esté lista."
                />
            </div>
            <div className="mt-4">
                <label htmlFor="constancia-dirigida" className="block text-sm font-medium text-gray-700 mb-1.5">
                    ¿Para quién es? <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <input
                    id="constancia-dirigida"
                    value={dirigidaA}
                    onChange={(e) => setDirigidaA(e.target.value)}
                    maxLength={200}
                    placeholder="Por ejemplo: Banco Mercantil, Embajada de España"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none"
                />
            </div>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                    type="submit"
                    disabled={enviando}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-sura-blue text-white font-semibold hover:bg-sura-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue disabled:opacity-50 transition-colors"
                >
                    {enviando && <Loader2 size={16} className="animate-spin" />}
                    {conSueldo ? 'Pedir a Gestión Humana' : 'Descargar constancia'}
                </button>
                <p className="text-sm text-gray-500">
                    Lleva un código para que quien la reciba confirme que es auténtica.
                </p>
            </div>
        </form>
    );
};

// ------------------------------------------------------------
// Preparar (GH)
// ------------------------------------------------------------

const PrepararModal = ({ constancia, onClose, onListo }) => {
    const [sueldo, setSueldo] = useState(constancia.salario_monto ? formatMonto(constancia.salario_monto) : '');
    const [contenido, setContenido] = useState(constancia.contenido_personalizado || '');
    const [firma, setFirma] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [rechazando, setRechazando] = useState(false);
    const [motivo, setMotivo] = useState('');
    const monto = parseMonto(sueldo);
    const faltaSueldo = constancia.incluir_salario && !monto;

    const emitir = async () => {
        setEnviando(true);
        try {
            const payload = { incluir_firma: firma, contenido_personalizado: contenido.trim() || null };
            if (constancia.incluir_salario) payload.salario_monto = monto;
            const res = await axiosInstance.put(`/api/constancias-trabajo/${constancia.id}/aprobar`, payload);
            toast.success(`Constancia de ${constancia.empleado_nombre} emitida`);
            onListo(res.data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo emitir la constancia');
        } finally {
            setEnviando(false);
        }
    };

    const rechazar = async () => {
        setEnviando(true);
        try {
            await axiosInstance.put(`/api/constancias-trabajo/${constancia.id}/rechazar`, { motivo_rechazo: motivo.trim() });
            toast.success('Constancia rechazada');
            setRechazando(false);
            onListo(null);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo rechazar');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <>
            <Modal isOpen={!rechazando} onClose={onClose} size="lg">
                <Modal.Header onClose={onClose}>Preparar constancia</Modal.Header>
                <Modal.Body>
                    <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                        <p className="font-semibold text-gray-900">{constancia.empleado_nombre}</p>
                        <p className="text-sm text-gray-600">
                            V-{constancia.empleado_cedula}{constancia.empleado_cargo ? `, ${constancia.empleado_cargo}` : ''}
                            {constancia.empleado_sede ? `, sede ${constancia.empleado_sede}` : ''}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                            Pidió una {nombreTipo(constancia).toLowerCase()}
                            {constancia.dirigida_a ? ` para ${constancia.dirigida_a}` : ''}
                            {' '}el {formatFecha(String(constancia.fecha_solicitud || '').slice(0, 10))}.
                        </p>
                    </div>
                    <div className="mt-5 space-y-5">
                        {constancia.incluir_salario && (
                            <CampoMonto id="preparar-sueldo" etiqueta="Sueldo mensual" valor={sueldo} onChange={setSueldo} />
                        )}
                        <div>
                            <label htmlFor="preparar-contenido" className="block text-sm font-medium text-gray-700 mb-1.5">
                                Párrafo adicional <span className="font-normal text-gray-400">(opcional)</span>
                            </label>
                            <textarea
                                id="preparar-contenido"
                                rows={3}
                                value={contenido}
                                onChange={(e) => setContenido(e.target.value)}
                                placeholder="Va debajo de los datos, antes del cierre"
                                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none resize-y"
                            />
                        </div>
                        <CheckFirma valor={firma} onChange={setFirma} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        onClick={() => setRechazando(true)}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                    >
                        Rechazar
                    </button>
                    <button
                        type="button"
                        onClick={emitir}
                        disabled={enviando || faltaSueldo}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sura-blue text-white text-sm font-semibold hover:bg-sura-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue disabled:opacity-50 transition-colors"
                    >
                        {enviando && <Loader2 size={15} className="animate-spin" />}
                        Emitir constancia
                    </button>
                </Modal.Footer>
            </Modal>
            <RechazoModal
                isOpen={rechazando}
                onClose={() => { setRechazando(false); setMotivo(''); }}
                onConfirm={rechazar}
                solicitud={{ codigo: nombreTipo(constancia), empleado_nombre: constancia.empleado_nombre }}
                motivo={motivo}
                setMotivo={setMotivo}
                submitting={enviando}
                titulo="Rechazar constancia"
            />
        </>
    );
};

// ------------------------------------------------------------
// Constancia de egreso (GH)
// ------------------------------------------------------------

const ConstanciaEgreso = ({ onEmitida }) => {
    const [egresados, setEgresados] = useState(null);
    const [empleadoId, setEmpleadoId] = useState('');
    const [sueldo, setSueldo] = useState('');
    const [dirigidaA, setDirigidaA] = useState('');
    const [firma, setFirma] = useState(true);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/empleados', { params: { activo: 'false' } })
            .then((r) => {
                const lista = (Array.isArray(r.data) ? r.data : []).filter((e) => e.fecha_egreso);
                lista.sort((a, b) => String(b.fecha_egreso).localeCompare(String(a.fecha_egreso)));
                setEgresados(lista);
            })
            .catch(() => setEgresados('error'));
    }, []);

    const monto = parseMonto(sueldo);
    const elegido = Array.isArray(egresados) ? egresados.find((e) => String(e.id) === String(empleadoId)) : null;

    const emitir = async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
            const res = await axiosInstance.post('/api/constancias-trabajo/egreso', {
                empleado_id: Number(empleadoId),
                salario_monto: monto,
                dirigida_a: dirigidaA.trim() || null,
                incluir_firma: firma,
            });
            await descargarConstancia(res.data);
            toast.success(`Constancia de egreso de ${res.data.empleado_nombre} emitida y descargada`);
            setEmpleadoId('');
            setSueldo('');
            setDirigidaA('');
            onEmitida();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo emitir la constancia');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <form onSubmit={emitir} className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-2xl space-y-5">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Constancia de egreso</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Para quien ya no trabaja en SURA: dice desde cuándo y hasta cuándo trabajó, y su último cargo.
                </p>
            </div>
            <div>
                <label htmlFor="egreso-empleado" className="block text-sm font-medium text-gray-700 mb-1.5">Egresado</label>
                <select
                    id="egreso-empleado"
                    value={empleadoId}
                    onChange={(e) => setEmpleadoId(e.target.value)}
                    disabled={!Array.isArray(egresados) || egresados.length === 0}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 bg-white focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none"
                >
                    <option value="">
                        {!egresados ? 'Cargando egresados...'
                            : egresados === 'error' ? 'No se pudieron cargar los egresados'
                                : egresados.length === 0 ? 'No hay egresados' : 'Elige a la persona'}
                    </option>
                    {(Array.isArray(egresados) ? egresados : []).map((e) => (
                        <option key={e.id} value={e.id}>
                            {e.nombre} (egresó el {formatFecha(e.fecha_egreso)})
                        </option>
                    ))}
                </select>
                {elegido && (
                    <p className="mt-1.5 text-sm text-gray-600">
                        {elegido.cargo || 'Sin cargo registrado'}{elegido.ficha ? `, ficha ${elegido.ficha}` : ''}
                    </p>
                )}
            </div>
            <CampoMonto id="egreso-sueldo" etiqueta="Último salario mensual (opcional)" valor={sueldo} onChange={setSueldo} />
            <div>
                <label htmlFor="egreso-dirigida" className="block text-sm font-medium text-gray-700 mb-1.5">
                    ¿Para quién es? <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <input
                    id="egreso-dirigida"
                    value={dirigidaA}
                    onChange={(e) => setDirigidaA(e.target.value)}
                    maxLength={200}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none"
                />
            </div>
            <CheckFirma valor={firma} onChange={setFirma} />
            <button
                type="submit"
                disabled={enviando || !empleadoId || (sueldo && !monto)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sura-blue text-white font-semibold hover:bg-sura-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue disabled:opacity-50 transition-colors"
            >
                {enviando && <Loader2 size={16} className="animate-spin" />}
                Emitir y descargar
            </button>
        </form>
    );
};

// ------------------------------------------------------------
// Vista
// ------------------------------------------------------------

const ConstanciaTrabajoView = () => {
    const { user } = useAuth();
    const isGH = ROLES_GH.includes(user?.role);
    const tieneFicha = Boolean(user?.empleado_id);

    const [activeTab, setActiveTab] = useState(tieneFicha ? 'mis' : 'preparar');
    const [loading, setLoading] = useState(true);
    const [mias, setMias] = useState([]);
    const [todas, setTodas] = useState([]);
    const [preparando, setPreparando] = useState(null);

    const [fQ, setFQ] = useState('');
    const [fEstado, setFEstado] = useState('');
    const [fDesde, setFDesde] = useState('');
    const [fHasta, setFHasta] = useState('');
    const [exportando, setExportando] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const pedidos = [
                tieneFicha
                    ? axiosInstance.get('/api/constancias-trabajo', { params: { empleado_id: user.empleado_id } })
                    : Promise.resolve({ data: [] }),
                isGH ? axiosInstance.get('/api/constancias-trabajo') : Promise.resolve({ data: [] }),
            ];
            const [rMias, rTodas] = await Promise.all(pedidos);
            setMias(Array.isArray(rMias.data) ? rMias.data : []);
            setTodas(Array.isArray(rTodas.data) ? rTodas.data : []);
        } catch (err) {
            toast.error('No se pudieron cargar las constancias');
        } finally {
            setLoading(false);
        }
    }, [isGH, tieneFicha, user?.empleado_id]);

    useEffect(() => { cargar(); }, [cargar]);

    const pendientes = useMemo(() => todas.filter((c) => c.estado === 'pendiente'), [todas]);
    const filtradas = useMemo(() => filtrarGH(
        todas,
        { q: fQ, estado: fEstado, desde: fDesde, hasta: fHasta },
        { nombre: (c) => c.empleado_nombre, estado: (c) => c.estado, fecha: (c) => c.fecha_solicitud },
    ), [todas, fQ, fEstado, fDesde, fHasta]);

    const exportar = async () => {
        if (filtradas.length === 0) { toast.error('No hay nada que exportar'); return; }
        setExportando(true);
        try {
            await descargarBlob(`/api/constancias-trabajo/export?ids=${filtradas.map((c) => c.id).join(',')}`, 'constancias.xlsx');
        } catch {
            toast.error('No se pudo exportar');
        } finally {
            setExportando(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <header className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Constancia de trabajo</h1>
                <p className="mt-1 text-gray-600">
                    Firmada por Gestión Humana y lista para el banco, la embajada o donde te la pidan.
                </p>
            </header>

            {isGH && (
                <div className="flex items-center gap-6 border-b border-gray-200 overflow-x-auto mb-6" role="tablist">
                    {tieneFicha && <TabButton label="Mis constancias" tab="mis" active={activeTab} onClick={setActiveTab} />}
                    <TabButton label="Por preparar" tab="preparar" active={activeTab} onClick={setActiveTab} badge={pendientes.length} />
                    <TabButton label="Todas" tab="todas" active={activeTab} onClick={setActiveTab} />
                    <TabButton label="Constancia de egreso" tab="egreso" active={activeTab} onClick={setActiveTab} icon={UserMinus} />
                </div>
            )}

            {activeTab === 'mis' && tieneFicha && (
                <div className="space-y-6">
                    <PedirConstancia onPedida={cargar} />
                    <section>
                        <h2 className="text-base font-semibold text-gray-900 mb-3">Mis constancias</h2>
                        {mias.length === 0 ? (
                            <EmptyState icon={FileCheck} message="Todavía no has pedido constancias" sub="La primera sale en segundos si no lleva sueldo" />
                        ) : (
                            <div className="space-y-3">
                                {mias.map((c) => <ConstanciaFila key={c.id} c={c} />)}
                            </div>
                        )}
                    </section>
                </div>
            )}

            {activeTab === 'preparar' && isGH && (
                <div className="space-y-3">
                    {pendientes.length === 0 ? (
                        <EmptyState icon={Clock} message="No hay constancias por preparar" sub="Las que piden con sueldo llegan aquí" />
                    ) : (
                        pendientes.map((c) => <ConstanciaFila key={c.id} c={c} showEmpleado onPreparar={setPreparando} />)
                    )}
                </div>
            )}

            {activeTab === 'todas' && isGH && (
                <div className="space-y-4">
                    <FiltrosGH
                        q={fQ} setQ={setFQ}
                        estado={fEstado} setEstado={setFEstado} estados={ESTADOS_CONSTANCIA}
                        desde={fDesde} setDesde={setFDesde} hasta={fHasta} setHasta={setFHasta}
                        onExport={exportar} exporting={exportando}
                        total={todas.length} mostrados={filtradas.length}
                    />
                    {filtradas.length === 0 ? (
                        <EmptyState icon={FileText} message={todas.length === 0 ? 'Todavía no hay constancias' : 'Ninguna coincide con los filtros'} />
                    ) : (
                        <div className="space-y-3">
                            {filtradas.map((c) => <ConstanciaFila key={c.id} c={c} showEmpleado onPreparar={setPreparando} />)}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'egreso' && isGH && <ConstanciaEgreso onEmitida={cargar} />}

            {!tieneFicha && !isGH && (
                <EmptyState icon={FileCheck} message="Tu usuario no está vinculado a una ficha" sub="Pídele a Gestión Humana que lo vincule para poder pedir constancias" />
            )}

            {preparando && (
                <PrepararModal
                    constancia={preparando}
                    onClose={() => setPreparando(null)}
                    onListo={() => { setPreparando(null); cargar(); }}
                />
            )}
        </div>
    );
};

export default ConstanciaTrabajoView;
